import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { normalizeLang } from "@/lib/i18n";

/*
 * Compact user menu shown in the header of authenticated pages.
 * Displays the user's first name (or email) + a dropdown with account actions.
 */

const DOWNLOAD_LABEL = {
  en: "My Reports & Downloads",
  "pt-BR": "Meus Relatórios e Downloads",
  "es-419": "Mis Informes y Descargas",
  "fr-CA": "Mes rapports et téléchargements",
};

const ACCOUNT_LABEL = {
  en: "Account settings",
  "pt-BR": "Configurações da conta",
  "es-419": "Configuración de la cuenta",
  "fr-CA": "Paramètres du compte",
};

export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const label = user.first_name || user.email;
  const lang = normalizeLang(user.preferred_language);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border border-[color:var(--border-stone)] px-3 py-1.5 text-[11px] font-medium tracking-normal normal-case text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]"
        data-testid="user-menu-button"
      >
        {label}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-60 rounded-xl border border-[color:var(--border-stone)] bg-white shadow-lg py-1 z-50 normal-case tracking-normal"
          data-testid="user-menu-dropdown"
        >
          <div className="px-3 py-2 text-[11px] text-[color:var(--text-secondary)] break-all">
            {user.email}
          </div>
          <div className="h-px bg-[color:var(--border-stone)]" />
          <a
            href="/account"
            className="block px-3 py-2 text-xs text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] font-medium"
            data-testid="user-menu-account"
          >
            {ACCOUNT_LABEL[lang] || ACCOUNT_LABEL.en}
          </a>
          <a
            href="/downloads"
            className="block px-3 py-2 text-xs text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] font-medium"
            data-testid="user-menu-downloads"
          >
            {DOWNLOAD_LABEL[lang] || DOWNLOAD_LABEL.en}
          </a>
          {user.is_admin && (
            <>
              <div className="h-px bg-[color:var(--border-stone)]" />
              <a
                href="/admin"
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)] font-medium"
                data-testid="user-menu-admin"
              >
                Admin dashboard
              </a>
              <a
                href="/admin/beta-codes"
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="user-menu-admin-beta"
              >
                Beta codes admin
              </a>
              <a
                href="/admin/users"
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="user-menu-admin-users"
              >
                Users admin
              </a>
              <a
                href="/admin/audit"
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="user-menu-admin-audit"
              >
                Audit log
              </a>
            </>
          )}
          <div className="h-px bg-[color:var(--border-stone)]" />
          <button
            type="button"
            onClick={logout}
            className="w-full text-left px-3 py-2 text-xs text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]"
            data-testid="user-menu-signout"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
